import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import Button from "@mui/material/Button"
import IconButton from "@mui/material/IconButton"
import Link from "@mui/material/Link"
import Snackbar from "@mui/material/Snackbar"
import TableCell from "@mui/material/TableCell"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import { format } from "date-fns"
import { useState } from "react"
import { Link as RouterLink } from "react-router-dom"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTrialEditDialog, {
  TRIAL_STATUS_OPTIONS,
} from "../components/AdminTrialEditDialog"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"

export const TRIAL_QUICK_EXTEND_DAYS = [7, 14]

export const TRIAL_FILTER_SPECS = {
  status: { param: "status" },
  ends: {
    toParams: (value) => {
      if (!value) return {}
      if (value === "expired") return { expired: "true" }
      return { ends_within: value }
    },
  },
  free: { param: "free_candidates" },
  plan: { param: "plan" },
  has_user: { param: "has_user" },
  user: { param: "user_id" },
}

const STATUS_GROUP_OPTIONS = [
  { value: "trial", label: "Trial" },
  { value: "live", label: "Live" },
  { value: "past_due", label: "Past due" },
  { value: "ended", label: "Ended" },
]

const TOOLBAR_FILTERS = [
  {
    key: "status",
    label: "Status",
    type: "multi",
    options: STATUS_GROUP_OPTIONS,
  },
  {
    key: "ends",
    label: "Ends",
    type: "select",
    options: [
      { value: "7", label: "Within 7 days" },
      { value: "14", label: "Within 14 days" },
      { value: "30", label: "Within 30 days" },
      { value: "expired", label: "Already ended" },
    ],
  },
  {
    key: "free",
    label: "Free candidates",
    type: "select",
    options: [
      { value: "zero", label: "None left" },
      { value: "some", label: "Some left" },
    ],
  },
  {
    key: "plan",
    label: "Plan",
    type: "select",
    options: [
      { value: "monthly", label: "Monthly" },
      { value: "annual", label: "Annual" },
    ],
  },
  {
    key: "has_user",
    label: "Linked user",
    type: "select",
    options: [
      { value: "true", label: "Has a user" },
      { value: "false", label: "No user" },
    ],
  },
  {
    key: "user",
    label: "User",
    type: "entity",
    entity: "account",
    placeholder: "Any user",
  },
]

function formatDate(value) {
  if (!value) return "—"
  return format(
    new Date(`${String(value).slice(0, 10)}T00:00:00`),
    "MMM d, yyyy"
  )
}

function formatStatus(value) {
  const match = TRIAL_STATUS_OPTIONS.find((option) => option.value === value)
  return match?.label || value || "—"
}

function TrialUserCell({ trial }) {
  const extra = Math.max((trial.user_count || 0) - 1, 0)
  const others = (trial.users || []).slice(1)
  if (!trial.email) {
    return (
      <TableCell sx={{ ...adminTableBodyCellSx, color: "text.disabled" }}>
        No linked user
      </TableCell>
    )
  }
  return (
    <TableCell sx={{ ...adminTableBodyCellSx, maxWidth: 280 }}>
      <Column gap={0.125} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {trial.user_name || "—"}
        </Typography>
        <Row gap={0.5} sx={{ alignItems: "center", minWidth: 0 }}>
          <Link
            component={RouterLink}
            to={`/admin/users?q=${encodeURIComponent(trial.email)}&active=all`}
            variant="caption"
            noWrap
            title="Open in Users"
          >
            {trial.email}
          </Link>
          {extra > 0 ? (
            <Tooltip title={others.map((user) => user.email).join(", ") || ""}>
              <Typography variant="caption" color="text.secondary">
                +{extra} more
              </Typography>
            </Tooltip>
          ) : null}
        </Row>
      </Column>
    </TableCell>
  )
}

export default function AdminTrialManagement() {
  const [editTrial, setEditTrial] = useState(null)
  const [saving, setSaving] = useState(false)
  const [extendingId, setExtendingId] = useState(null)
  const [notice, setNotice] = useState(null)

  // No default sort: the API puts the soonest-ending terms first.
  const query = useAdminPaginatedQuery(adminApi.fetchTrialManagement, {
    filters: TRIAL_FILTER_SPECS,
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  async function handleSaveTrial(form) {
    if (!editTrial) return
    setSaving(true)
    try {
      await adminApi.updateTrialManagement(editTrial.id, form)
      setEditTrial(null)
      refresh()
    } finally {
      setSaving(false)
    }
  }

  async function handleExtend(trial, days) {
    setExtendingId(trial.id)
    try {
      const { data } = await adminApi.updateTrialManagement(trial.id, {
        extend_days: days,
      })
      setNotice(
        `Extended ${trial.email || `#${trial.id}`} to ${formatDate(
          data?.current_term_ends_at
        )}`
      )
      refresh()
    } catch (error) {
      setNotice(
        error?.response?.data?.extend_days ||
          error?.response?.data?.detail ||
          "Could not extend."
      )
    } finally {
      setExtendingId(null)
    }
  }

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Trial management"
        subtitle="Zoho subscriptions: find trials ending soon, extend them, or edit dates and free candidates."
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search name, email or Zoho ID"
        noun={totalResultsSize === 1 ? "subscription" : "subscriptions"}
        filters={TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={7}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="name"
              label="Name / Email"
              query={query}
              minWidth={{ xs: 140, sm: 220 }}
            />
            <AdminSortableHeaderCell
              columnName="plan"
              label="Plan"
              query={query}
              minWidth={{ xs: 64, sm: 88 }}
            />
            <AdminSortableHeaderCell
              columnName="status"
              label="Status"
              query={query}
              minWidth={{ xs: 72, sm: 110 }}
            />
            <AdminSortableHeaderCell
              columnName="current_term_ends_at"
              label="Trial ends on"
              query={query}
              minWidth={{ xs: 88, sm: 120 }}
            />
            <AdminSortableHeaderCell
              columnName="expires_at"
              label="Valid until"
              query={query}
              minWidth={{ xs: 88, sm: 120 }}
            />
            <AdminSortableHeaderCell
              columnName="free_candidates_remaining"
              label="Free candidates"
              query={query}
              align="right"
              minWidth={{ xs: 72, sm: 120 }}
            />
            <AdminHeaderTableCell align="right" minWidth={{ xs: 140, sm: 180 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((trial) => (
          <AdminTableRow key={trial.id} hover>
            <TrialUserCell trial={trial} />
            <AdminTruncatedCell
              value={
                trial.plan
                  ? trial.plan[0].toUpperCase() + trial.plan.slice(1)
                  : "—"
              }
            />
            <AdminTruncatedCell value={formatStatus(trial.status)} />
            <AdminTruncatedCell
              value={formatDate(trial.current_term_ends_at)}
            />
            <AdminTruncatedCell value={formatDate(trial.expires_at)} />
            <AdminTruncatedCell
              value={trial.free_candidates_remaining ?? "—"}
              align="right"
            />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <Row
                gap={0.5}
                justifyContent="flex-end"
                sx={{ flexWrap: "nowrap" }}
              >
                {TRIAL_QUICK_EXTEND_DAYS.map((days) => (
                  <Tooltip
                    key={days}
                    title={`Push the trial end and valid-until dates ${days} days out (from today if already past)`}
                  >
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={extendingId === trial.id}
                        onClick={() => handleExtend(trial, days)}
                        sx={{ minWidth: 0, px: 1 }}
                      >
                        +{days}d
                      </Button>
                    </span>
                  </Tooltip>
                ))}
                <IconButton
                  size="small"
                  aria-label="Edit trial"
                  onClick={() => setEditTrial(trial)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Row>
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <AdminTrialEditDialog
        open={Boolean(editTrial)}
        trial={editTrial}
        onClose={() => setEditTrial(null)}
        onSave={handleSaveTrial}
        saving={saving}
      />
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={3000}
        onClose={() => setNotice(null)}
        message={notice ? String(notice) : ""}
      />
    </Column>
  )
}
